import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workAuthService } from '@/services/workAuthService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  RefreshCw,
  Download,
  Archive,
  Clock,
  Plus,
  CheckCircle,
  XCircle,
  Calendar
} from 'lucide-react';

interface BackupInfo {
  filename: string;
  date: string;
  path: string;
}

interface BackupStatus {
  isScheduled: boolean;
  isRunning: boolean;
  lastRun: string | null;
  nextRun: string | null;
  timezone: string;
  lastBackupResult: {
    success: boolean;
    filename: string;
    timestamp: string;
    rowCount: number;
    sizeBytes: number;
    error?: string;
  } | null;
}

const SmartsheetBackupsPage: React.FC = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [backupsResponse, statusResponse] = await Promise.all([
        workAuthService.listBackups(),
        workAuthService.getBackupStatus()
      ]);

      if (backupsResponse.success) {
        setBackups(backupsResponse.data);
      }

      if (statusResponse.success) {
        setStatus(statusResponse.data);
      }
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError(err instanceof Error ? err.message : 'Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    fetchData();
  };

  const handleTriggerBackup = async () => {
    setTriggerLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await workAuthService.triggerBackup();
      if (response.success && response.data.success) {
        setSuccessMessage(`Backup created successfully: ${response.data.filename} (${response.data.rowCount} rows, ${(response.data.sizeBytes / 1024).toFixed(2)} KB)`);
        fetchData(); // Refresh the list
      } else {
        setError(response.data.error || 'Backup failed');
      }
    } catch (err) {
      console.error('Error triggering backup:', err);
      setError(err instanceof Error ? err.message : 'Failed to create backup');
    } finally {
      setTriggerLoading(false);
    }
  };

  const handleDownload = async (filename: string) => {
    setDownloadingFile(filename);
    setError(null);
    try {
      const response = await workAuthService.getBackupDownloadUrl(filename);
      if (response.success) {
        // Open the presigned URL in a new tab to download
        window.open(response.data.url, '_blank');
      } else {
        setError('Failed to get download URL');
      }
    } catch (err) {
      console.error('Error downloading backup:', err);
      setError(err instanceof Error ? err.message : 'Failed to download backup');
    } finally {
      setDownloadingFile(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatBackupDate = (dateStr: string) => {
    // dateStr is in format YYYY-MM-DD
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading && backups.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/hr/work-auth/dashboard')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleTriggerBackup}
            disabled={triggerLoading || (status?.isRunning ?? false)}
          >
            {triggerLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Create Backup Now
          </Button>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Status Card */}
      {status && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Backup Schedule</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Next Backup</div>
                  <div className="font-medium">
                    {status.nextRun ? formatDate(status.nextRun) : 'Not scheduled'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Last Run</div>
                  <div className="font-medium">
                    {status.lastRun ? formatDate(status.lastRun) : 'Never'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Archive className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Status</div>
                  <div className="font-medium">
                    {status.isRunning ? (
                      <Badge variant="secondary">Running...</Badge>
                    ) : status.isScheduled ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Scheduled
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not Scheduled</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Weekly backups are scheduled for Sunday at 2:00 AM ({status.timezone})
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backups List */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Archive className="h-6 w-6 text-blue-600" />
            <div>
              <CardTitle className="text-xl">Smartsheet Backups</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Weekly CSV exports of work authorization data
                {backups.length > 0 && ` (${backups.length} backup${backups.length > 1 ? 's' : ''})`}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Archive className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No backups yet</p>
              <p className="text-sm mt-1">Create a backup using the button above or wait for the weekly schedule</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Backup Date
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                        Filename
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {backups.map((backup) => (
                      <tr
                        key={backup.filename}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium">
                            {formatBackupDate(backup.date)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {backup.filename}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(backup.filename)}
                            disabled={downloadingFile === backup.filename}
                          >
                            {downloadingFile === backup.filename ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartsheetBackupsPage;
